import { useLayoutEffect, useMemo, useReducer } from 'react';
import { LocationProvider as OriginLocationProvider } from 'preact-iso';

const OriginLocationContext = OriginLocationProvider.ctx;

/**
 * Custom LocationProvider implementation, preact-iso {@link OriginLocationProvider} listens for clicks on same-origin {@code <a href>} links
 * and navigates by updating history. Mojito routing owns navigation, so that click handling is
 * omitted here. This provider exposes the same location context excluding an anchor click handling.
 *
 * @function LocationProvider
 * @param {object} props - Provider props.
 * @param {string} [props.url] - Initial URL path; defaults to the current browser location.
 * @param {node} [props.children] - Tree rendered inside the preact-iso location context.
 * @returns {React.ReactElement} Location context provider element.
 * @memberof Mojito.Applications.Common
 */
export default function LocationProvider(props) {
    const [url, navigate] = useReducer(
        doNavigation,
        props.url || location.pathname + location.search
    );

    const locationContext = useMemo(() => {
        const parsedUrl = new URL(url, location.origin);
        const path = parsedUrl.pathname.replace(/\/+$/g, '') || '/';
        const query = Object.fromEntries(parsedUrl.searchParams);
        const route = (nextUrl, replace) => navigate({ url: nextUrl, replace });
        return { url, path, query, route, wasPush };
    }, [url]);

    useLayoutEffect(() => {
        addEventListener('popstate', navigate);

        return () => {
            removeEventListener('popstate', navigate);
        };
    }, []);

    return (
        <OriginLocationContext.Provider value={locationContext}>
            {props.children}
        </OriginLocationContext.Provider>
    );
}

let wasPush = false;

function doNavigation(state, action) {
    if (action?.url) {
        wasPush = !action.replace;
        if (wasPush) {
            history.pushState(null, '', action.url);
        } else {
            history.replaceState(null, '', action.url);
        }
        return action.url;
    }

    wasPush = false;
    return location.pathname + location.search;
}
